import React from 'react';
import { 
  formatDuration, 
  formatCost, 
  formatNumber, 
  formatUsageQuota,
  formatDate 
} from '../utils/consolidatedAnalytics.js';
import { MetricsGridSkeleton } from './AnalyticsLoadingSkeleton';
import AnalyticsErrorDisplay from './AnalyticsErrorDisplay';
import { isFallbackData, FallbackContent } from '../utils/analyticsFallbacks';
import { 
  createAccessibleLiveRegion,
  generateDataUpdateAnnouncement,
  createAccessibleProgressAttributes
} from '../utils/consolidatedAccessibility.js';
import { getMetricsGridClasses, getMetricCardClasses } from '../utils/consolidatedAnalytics.js';
import { getWarningClasses } from '../utils/statusUtils.js';
import { useTheme } from '../contexts/ThemeContext';

const DashboardStatistics = ({ 
  analytics, 
  loading, 
  error, 
  onRetry, 
  canRetry = false,
  isRetrying = false,
  onClearError,
  previousAnalytics = null
}) => {
  const { theme } = useTheme();
  const metricCardClasses = getMetricCardClasses();
  const warningClasses = getWarningClasses(theme);
  
  // Create live region for screen reader announcements
  const liveRegionProps = createAccessibleLiveRegion('polite', false);
  
  if (loading) {
    return (
      <div role="status" aria-label="Loading dashboard statistics">
        <MetricsGridSkeleton columns={4} />
        <span className="sr-only">Loading dashboard statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <AnalyticsErrorDisplay
        error={error}
        onRetry={onRetry}
        onClearError={onClearError}
        canRetry={canRetry}
        isRetrying={isRetrying}
        className="mb-6"
        role="alert"
        aria-live="assertive"
      />
    );
  }

  if (!analytics) {
    return (
      <div role="status" aria-label="Dashboard metrics unavailable">
        <FallbackContent.MetricsUnavailable message="Dashboard metrics are currently unavailable" />
      </div>
    );
  }

  const { metrics, timeRange } = analytics;
  const isUsingFallbackData = isFallbackData(analytics);
  
  // Check if metrics data is available
  if (!metrics) {
    return (
      <div role="status" aria-label="Dashboard metrics unavailable">
        <FallbackContent.MetricsUnavailable message="Dashboard metrics are currently unavailable" />
      </div>
    );
  }
  
  // Generate announcement for screen readers when data updates
  const dataUpdateAnnouncement = previousAnalytics 
    ? generateDataUpdateAnnouncement('metrics', metrics, previousAnalytics.metrics)
    : null;
  
  // Calculate minutes used from total duration (with fallback for undefined values)
  const minutesUsed = Math.ceil((metrics.totalDuration || 0) / 60);
  
  // Determine next billing date (this would typically come from user subscription data)
  const nextBillingDate = timeRange?.type === 'billing_period' && timeRange?.billingDaysRemaining !== null
    ? new Date(Date.now() + ((timeRange.billingDaysRemaining || 0) * 24 * 60 * 60 * 1000)).toISOString().split('T')[0]
    : 'N/A';

  const stats = [
    { 
      name: 'Total Calls', 
      stat: formatNumber(metrics.totalCalls || 0),
      description: `${formatNumber(metrics.successfulCalls || 0)} successful, ${formatNumber(metrics.failedCalls || 0)} failed`,
      ariaLabel: `Total calls: ${formatNumber(metrics.totalCalls || 0)}. ${formatNumber(metrics.successfulCalls || 0)} successful, ${formatNumber(metrics.failedCalls || 0)} failed`,
      icon: '📞'
    },
    { 
      name: 'Avg. Call Duration', 
      stat: formatDuration(metrics.averageDuration || 0),
      description: `Total: ${formatDuration(metrics.totalDuration || 0)}`,
      ariaLabel: `Average call duration: ${formatDuration(metrics.averageDuration || 0)}. Total duration: ${formatDuration(metrics.totalDuration || 0)}`,
      icon: '⏱️'
    },
    { 
      name: 'Minutes Used', 
      stat: formatUsageQuota(minutesUsed, (metrics.remainingQuota?.minutes || 0)),
      description: `${formatNumber(metrics.remainingQuota?.minutes || 0)} minutes remaining`,
      ariaLabel: `Minutes used: ${minutesUsed} out of ${minutesUsed + (metrics.remainingQuota?.minutes || 0)}. ${formatNumber(metrics.remainingQuota?.minutes || 0)} minutes remaining`,
      icon: '📊',
      progress: (metrics.remainingQuota?.minutes || 0) > 0 ? (minutesUsed / (minutesUsed + (metrics.remainingQuota?.minutes || 0))) * 100 : 0
    },
    { 
      name: timeRange?.type === 'billing_period' ? 'Next Billing Date' : 'Total Cost', 
      stat: timeRange?.type === 'billing_period' ? nextBillingDate : formatCost(metrics.totalCost || 0),
      description: timeRange?.type === 'billing_period' 
        ? `${timeRange.billingDaysRemaining || 0} days remaining`
        : 'Current period cost',
      ariaLabel: timeRange?.type === 'billing_period' 
        ? `Next billing date: ${nextBillingDate}. ${timeRange.billingDaysRemaining || 0} days remaining`
        : `Total cost: ${formatCost(metrics.totalCost || 0)} for current period`,
      icon: timeRange?.type === 'billing_period' ? '📅' : '💰'
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Live region for screen reader announcements */}
      {dataUpdateAnnouncement && (
        <div {...liveRegionProps}>
          {dataUpdateAnnouncement}
        </div>
      )}
      
      {/* Fallback data warning */}
      {isUsingFallbackData && (
        <div 
          className={warningClasses.container}
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-center">
            <svg 
              className={warningClasses.icon} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className={warningClasses.content}>
              <p className={warningClasses.title}>
                Data Unavailable
              </p>
              <p className={warningClasses.message}>
                {analytics.message || 'Showing placeholder data while analytics service is unavailable'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Metrics grid */}
      <dl 
        className={getMetricsGridClasses(stats.length)}
        role="list"
        aria-label="Dashboard statistics"
      >
        {stats.map((item, index) => (
          <div 
            key={item.name}
            role="listitem"
            className={`${metricCardClasses.container} ${
              isUsingFallbackData 
                ? 'opacity-75 bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                : ''
            }`}
            aria-label={item.ariaLabel}
            tabIndex={0}
          >
            <dt className={`${metricCardClasses.title} flex items-center`}>
              <span className="mr-2 text-lg" aria-hidden="true">{item.icon}</span>
              {item.name}
            </dt>
            <dd className={metricCardClasses.value}>
              {item.stat}
            </dd>
            {item.description && (
              <dd className={metricCardClasses.description}>
                {item.description}
              </dd>
            )}
            {/* Progress bar for usage metrics */}
            {item.progress !== undefined && (
              <div className="mt-2">
                <div 
                  className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2"
                  role="progressbar"
                  {...createAccessibleProgressAttributes(item.progress, 100, `Usage: ${item.progress.toFixed(1)}%`)}
                >
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      item.progress > 80 ? 'bg-red-500' : 
                      item.progress > 60 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(item.progress, 100)}%` }}
                  />
                </div>
                <span className="sr-only">
                  Usage at {item.progress.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
};

export default DashboardStatistics;
